import {
  Globe,
  Smartphone,
  Megaphone,
  Briefcase,
  Palette,
  Search,
} from 'lucide-react';

export const COMPANY = {
  name: 'BSPL Digital Solutions',
  tagline: 'Fresh Ideas, Modern Solutions',
  email: 'info@bspl.online',
  phone: '+91 9217282344',
  phoneTel: '+919217282344',
  whatsapp: '919217282344',
  location: 'Delhi, India',
  hours: 'Mon - Sun: 9:30am - 7:30pm',
  social: {
    linkedin:
      'https://www.linkedin.com/company/bspl-%E2%80%93-intelligent-business-systems/',
  },
};

export const NAV_LINKS = [
  { path: '/', label: 'Home' },
  { path: '/services', label: 'Services' },
  { path: '/about', label: 'About' },
  { path: '/portfolio', label: 'Portfolio' },
  { path: '/contact', label: 'Contact' },
];

export const MARQUEE_ITEMS = [
  'WEB DEVELOPMENT',
  'MOBILE APPS',
  'DIGITAL MARKETING',
  'IT CONSULTANCY',
  'UI/UX DESIGN',
];

export const SERVICES = [
  {
    title: 'Web Development',
    description:
      'Custom websites and web apps built for speed, SEO, and conversions — from corporate sites to full-stack platforms.',
    icon: Globe,
    iconBg: 'bg-[#FFF0EB]',
    iconColor: 'text-[#FF4500]',
    href: '/services',
  },
  {
    title: 'App Development',
    description:
      'Native and cross-platform mobile apps with polished UX, secure backends, and scalable architecture.',
    icon: Smartphone,
    iconBg: 'bg-[#EBF0FF]',
    iconColor: 'text-[#0052FF]',
    href: '/services',
  },
  {
    title: 'Digital Marketing',
    description:
      'SEO, social, PPC, and content strategies that grow your brand visibility and qualified leads.',
    icon: Megaphone,
    iconBg: 'bg-[#FFFBE6]',
    iconColor: 'text-[#B8860B]',
    href: '/services',
  },
  {
    title: 'IT Consultancy',
    description:
      'Technology roadmaps, cloud migration, and digital transformation guidance tailored to your business.',
    icon: Briefcase,
    iconBg: 'bg-[#F0F4FF]',
    iconColor: 'text-[#0052FF]',
    href: '/services',
  },
  {
    title: 'UI/UX Design',
    description:
      'Research-driven interfaces, wireframes, and design systems that users love and businesses trust.',
    icon: Palette,
    iconBg: 'bg-[#FFF0EB]',
    iconColor: 'text-[#FF4500]',
    href: '/services',
  },
  {
    title: 'SEO & Performance',
    description:
      'Technical SEO, Core Web Vitals, and speed optimization so your site ranks and loads fast.',
    icon: Search,
    iconBg: 'bg-[#F7F7F5]',
    iconColor: 'text-[#0A0A0A]',
    href: '/services',
  },
];

export const STATS = [
  { value: 9, suffix: '+', label: 'Years Experience' },
  { value: 50, suffix: '+', label: 'Happy Clients' },
  { value: 99, suffix: '%', label: 'Client Satisfaction' },
  { value: 2, suffix: '+', label: 'Premium Client Sites' },
];

export const PROCESS_STEPS = [
  {
    step: 1,
    title: 'Discovery',
    description:
      'We learn your goals, audience, and constraints — then define scope, timeline, and success metrics.',
  },
  {
    step: 2,
    title: 'Design',
    description:
      'Wireframes and visual design aligned to your brand, with iterative feedback before development.',
  },
  {
    step: 3,
    title: 'Build',
    description:
      'Agile development with regular demos, clean code, and performance baked in from day one.',
  },
  {
    step: 4,
    title: 'Launch',
    description:
      'Deployment, QA, training, and post-launch support so you ship with confidence.',
  },
];

export const PORTFOLIO_PROJECTS = [
  {
    id: 'piquant',
    name: 'Piquant Scientific',
    tagline: 'Precision in Innovation',
    category: 'Scientific Equipment & Laboratory Solutions',
    tags: ['Web Design', 'Scientific', 'SEO'],
    image: `${process.env.PUBLIC_URL}/portfolio/piquant-cover.png`,
    cover: `${process.env.PUBLIC_URL}/portfolio/piquant-cover.png`,
    logo: `${process.env.PUBLIC_URL}/portfolio/piquant-logo.png`,
    href: 'https://piquantscientific.com',
    site: 'piquantscientific.com',
    description:
      'Homepage for a premium lab instruments brand — gold-accent hero, catalog CTAs, and trust signals for research institutions across India.',
  },
  {
    id: 'ezilt',
    name: 'EZILT Lifesciences',
    tagline: 'Empowering Human Discovery',
    category: 'Life Science & Laboratory Equipment',
    tags: ['Web Development', 'Life Sciences', 'UI/UX'],
    image: `${process.env.PUBLIC_URL}/portfolio/ezilt-cover.png`,
    cover: `${process.env.PUBLIC_URL}/portfolio/ezilt-cover.png`,
    logo: `${process.env.PUBLIC_URL}/portfolio/ezilt-logo.png`,
    logoWide: true,
    href: 'https://ezilt.in',
    site: 'ezilt.in',
    description:
      'Homepage and product experience for a life science brand — hero messaging, lab imagery, and clear paths to explore bioprocess and diagnostic solutions.',
  },
];

export const FAQS = [
  {
    question: 'How long does it take to build a website?',
    answer:
      'A brochure website typically takes 2–4 weeks; e-commerce or custom web apps often run 6–8 weeks. We share a clear timeline after discovery based on your scope and priorities.',
  },
  {
    question: 'Do you provide maintenance after launch?',
    answer:
      'Yes. We offer ongoing support packages — security updates, backups, content changes, and performance monitoring so your site stays fast and secure.',
  },
  {
    question: 'Will my website be mobile-friendly?',
    answer:
      'Every site we build is fully responsive with a mobile-first approach, so it looks and works great on phones, tablets, and desktops — essential for SEO and conversions.',
  },
  {
    question: 'Can you help with SEO and digital marketing?',
    answer:
      'Absolutely. We offer SEO, social media, content, and paid campaigns to improve rankings, visibility, and qualified leads for your business.',
  },
  {
    question: 'What if I need changes after launch?',
    answer:
      'No problem. We offer flexible support to add features, update content, or refine design as your business evolves — just reach out anytime.',
  },
  {
    question: 'Do you work with clients outside India?',
    answer:
      'Yes — we work with clients in the USA, UK, UAE, Australia, and beyond, with async updates and video calls across time zones.',
  },
];

export const TESTIMONIALS = [
  {
    quote:
      'BSPL built a modern platform that showcases our laboratory products and research expertise — clean design, fast performance, and strong SEO from day one.',
    name: 'EZILT Lifesciences',
    href: 'https://ezilt.in',
    site: 'ezilt.in',
  },
  {
    quote:
      'Our new scientific website reflects the precision of our brand. The team delivered a credible, conversion-focused experience we are proud to share.',
    name: 'Piquant Scientific',
    href: 'https://piquantscientific.com',
    site: 'piquantscientific.com',
  },
];
